import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import type { Notification } from '@xdocs/schemas';
import { EmptyState, Skeleton, cn } from '@xdocs/ui';
import { NotificationItem, routeForRef } from '../../features/notifications/NotificationItem.js';
import { useExpandedGroups } from '../../features/notifications/useExpandedGroups.js';
import { useNotifications } from '../../features/notifications/useNotifications.js';
import { useNotificationsT } from '../../features/notifications/useNotificationsT.js';
import styles from './NotificationsPage.module.css';

const SKELETON_ROW_COUNT = 4;

/**
 * `/notifications` (F33, design/app/32-notifications.html): the dedicated
 * full-page notifications feed — same data/rows as the topbar
 * `NotificationsBell` panel (`features/notifications/NotificationItem.tsx`
 * is shared by both), rendered as its own page rather than an anchored
 * panel. The bell stays the quick-glance entry point; this page is the
 * "see everything" destination the mock's H1 + "Mark all read" header row
 * + single list card depict — no unread/all filter here (the mock's page
 * header carries only the title and the mark-all-read action).
 */
export function NotificationsPage(): ReactElement {
  const { t } = useNotificationsT();
  const navigate = useNavigate();
  const { items, isLoading, isError, refetch, hasMore, loadMore, isLoadingMore, loadMoreError, markRead } =
    useNotifications();
  const { isExpanded, toggle: toggleExpanded } = useExpandedGroups();

  const isEmpty = !isLoading && !isError && items.length === 0;

  function handleOpenItem(item: Notification): void {
    if (item.readAt === null) void markRead(item.id);
    const route = routeForRef(item.refType, item.refId);
    if (route) void navigate(route);
  }

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <h1 className={cn(styles['title'])}>{t('notifications:panel.title')}</h1>
        <button type="button" className={cn(styles['markAllRead'])} onClick={() => void markRead(null)}>
          {t('notifications:panel.markAllRead')}
        </button>
      </div>

      <div className={cn(styles['card'])} aria-busy={isLoading || undefined}>
        {isError && (
          <EmptyState kind="error" title={t('notifications:error.title')} action={{ label: t('notifications:error.retry'), onClick: refetch }} />
        )}

        {!isError && isLoading && (
          <div className={cn(styles['skeletonBlock'])} aria-hidden="true">
            <Skeleton shape="row" count={SKELETON_ROW_COUNT} />
          </div>
        )}

        {!isError && isEmpty && <EmptyState kind="first-use" title={t('notifications:empty.title')} description={t('notifications:empty.description')} />}

        {!isError && !isEmpty && (
          <ul className={cn(styles['list'])}>
            {items.map((item) => (
              <NotificationItem
                key={item.id}
                item={item}
                onOpen={handleOpenItem}
                expanded={isExpanded(item.id)}
                onToggleExpand={toggleExpanded}
                wrapperClassName={styles['row']}
                variant="flush"
              />
            ))}
          </ul>
        )}
      </div>

      {!isError && !isEmpty && hasMore && (
        <div className={cn(styles['loadMore'])}>
          {loadMoreError ? (
            <p role="alert" className={cn(styles['loadMoreError'])}>
              {loadMoreError}{' '}
              <button type="button" className={cn(styles['loadMoreRetry'])} onClick={loadMore}>
                {t('notifications:error.retry')}
              </button>
            </p>
          ) : (
            <button
              type="button"
              className={cn(styles['loadMoreButton'])}
              disabled={isLoadingMore}
              aria-busy={isLoadingMore || undefined}
              onClick={loadMore}
            >
              {t('notifications:loadMore')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
